import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CHART_COLORS } from "@/lib/constants";
import { Users, MapPin, Briefcase, GraduationCap, VenetianMask, ArrowRight, Building2, CreditCard } from "lucide-react";
import PictogramRender from "./PictogramRender";
import useChartPref from "@/hooks/useChartPref";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const ChartTypeSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-[105px] ml-auto text-xs" data-testid={testid}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="batang">Batang</SelectItem>
      <SelectItem value="pie">Pie</SelectItem>
      <SelectItem value="orang">Orang</SelectItem>
    </SelectContent>
  </Select>
);

// Objek props statis di level modul agar tidak dibuat ulang tiap render (hindari re-render Recharts)
const PIE_MARGIN = { top: 0, bottom: 4 };
const PIE_LEGEND_STYLE = { fontSize: 11, lineHeight: "16px", maxHeight: 64, overflowY: "auto", width: "100%" };
const HBAR_MARGIN = { left: 10, right: 20 };
const VBAR_MARGIN = { left: 0, right: 10 };
const TICK_12 = { fontSize: 12, fill: "#64748B" };
const TICK_11 = { fontSize: 11, fill: "#64748B" };
const CURSOR_FILL = { fill: "#F8FAFC" };
const RADIUS_RIGHT = [0, 4, 4, 0];
const RADIUS_TOP = [4, 4, 0, 0];

const PieRender = ({ data, inner = 0, height = 260 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <PieChart margin={PIE_MARGIN}>
      <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="42%"
        innerRadius={inner ? 38 : 0} outerRadius={62} label>
        {data.map((entry, i) => <Cell key={entry.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
      </Pie>
      <Tooltip />
      <Legend verticalAlign="bottom" wrapperStyle={PIE_LEGEND_STYLE} />
    </PieChart>
  </ResponsiveContainer>
);

const BarRender = ({ data, color = "#2563EB", height = 210 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} layout="vertical" margin={HBAR_MARGIN}>
      <CartesianGrid horizontal={false} stroke="#F1F5F9" />
      <XAxis type="number" allowDecimals={false} tick={TICK_12} />
      <YAxis type="category" dataKey="name" width={110} tick={TICK_11} />
      <Tooltip cursor={CURSOR_FILL} />
      <Bar dataKey="value" fill={color} radius={RADIUS_RIGHT} barSize={18} />
    </BarChart>
  </ResponsiveContainer>
);

const StatCard = ({ icon: Icon, label, value, tone, testid, onClick }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-rose-50 text-rose-600",
  };
  return (
    <Card onClick={onClick} data-testid={testid}
      className={`p-6 border-slate-200 shadow-none rounded-lg ${onClick ? "cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors group" : ""}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 flex items-center gap-1.5">
            {label}
            {onClick && <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />}
          </p>
          <p className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-2">{value}</p>
        </div>
        <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
};

const ChartCard = ({ icon: Icon, title, children, testid, extra, className = "" }) => (
  <Card className={`p-6 border-slate-200 shadow-none rounded-lg ${className}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-5">
      <Icon className="h-4 w-4 text-blue-600" />
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {extra}
    </div>
    {children}
  </Card>
);

const EmptyChart = () => (
  <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">Belum ada data</div>
);

const PieBlock = ({ icon: Icon, title, data, inner = 0, testid, barColor = "#2563EB" }) => {
  const [tipe, setTipe] = useChartPref(testid, "pie");
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-blue-600" />
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        <ChartTypeSelect value={tipe} onChange={setTipe} testid={`${testid}-tipe`} />
      </div>
      {data.length
        ? (tipe === "pie" ? <PieRender data={data} inner={inner} /> : tipe === "orang" ? <PictogramRender data={data} /> : <BarRender data={data} color={barColor} />)
        : <EmptyChart />}
    </div>
  );
};


// Blok statistik Data Pekerja (Total, Lokasi, Jabatan, Pendidikan, Gender) — dipakai Beranda area & Master (gabungan)
export default function PekerjaCharts({ data, onNavigate, tLokasi, setTLokasi, tJabatan, setTJabatan }) {
  const [tArea, setTArea] = useChartPref("area");
  return (<>
      {data.by_area && (
        <ChartCard icon={Building2} title="Pekerja per Area" testid="chart-area" className="px-2 sm:px-3"
          extra={<ChartTypeSelect value={tArea} onChange={setTArea} testid="chart-area-tipe" />}>
          {!data.by_area.length ? <EmptyChart /> : tArea === "pie" ? <PieRender data={data.by_area} /> : tArea === "orang" ? <PictogramRender data={data.by_area} /> : <BarRender data={data.by_area} color="#F59E0B" />}
        </ChartCard>
      )}
      <StatCard icon={Users} label="Total Pekerja" value={data.aktif} tone="blue" testid="stat-total"
        onClick={() => onNavigate?.("pekerja")} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <ChartCard icon={MapPin} title="Lokasi Kerja" testid="chart-lokasi" className="md:col-span-2 px-2 sm:px-3"
          extra={<ChartTypeSelect value={tLokasi} onChange={setTLokasi} testid="chart-lokasi-tipe" />}>
          {!data.by_lokasi.length ? <EmptyChart />
            : tLokasi === "pie" ? <PieRender data={data.by_lokasi} height={360} />
            : tLokasi === "orang" ? <PictogramRender data={data.by_lokasi} height={520} maxIkon={25} />
            : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.by_lokasi} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={TICK_11} interval={0} angle={-40} textAnchor="end" height={110} />
                <YAxis allowDecimals={false} tick={TICK_12} />
                <Tooltip cursor={CURSOR_FILL} />
                <Bar dataKey="value" fill="#2563EB" radius={RADIUS_TOP} barSize={22} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard icon={Briefcase} title="Jabatan" testid="chart-jabatan" className="md:col-span-2 px-2 sm:px-3"
          extra={<ChartTypeSelect value={tJabatan} onChange={setTJabatan} testid="chart-jabatan-tipe" />}>
          {!data.by_jabatan.length ? <EmptyChart />
            : tJabatan === "pie" ? <PieRender data={data.by_jabatan} height={320} />
            : tJabatan === "orang" ? <PictogramRender data={data.by_jabatan} height={520} maxIkon={25} />
            : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.by_jabatan} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={TICK_11} interval={0} angle={-40} textAnchor="end" height={110} />
                <YAxis allowDecimals={false} tick={TICK_12} />
                <Tooltip cursor={CURSOR_FILL} />
                <Bar dataKey="value" fill="#10B981" radius={RADIUS_TOP} barSize={22} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <Card className="p-6 border-slate-200 shadow-none rounded-lg md:col-span-2" data-testid="chart-pendidikan-gender">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <PieBlock icon={GraduationCap} title="Pendidikan"
              data={data.by_pendidikan} testid="chart-pendidikan" barColor="#2563EB" />
            <div className="sm:border-l sm:border-slate-100 sm:pl-4">
              <PieBlock icon={VenetianMask} title="Gender"
                data={data.by_gender} inner={45} testid="chart-gender" barColor="#10B981" />
            </div>
            <div className="sm:border-l sm:border-slate-100 sm:pl-4">
              <PieBlock icon={CreditCard} title="Penerima Pertalife"
                data={data.by_pertalife || []} inner={45} testid="chart-pertalife" barColor="#F59E0B" />
              {data.by_pertalife?.length > 0 && (() => { const tot = data.by_pertalife.reduce((s, x) => s + x.value, 0); const get = (n) => data.by_pertalife.find((x) => x.name === n)?.value || 0; const tdk = get("TIDAK DIDAFTARKAN"); return (
                <p className="mt-2 text-xs text-slate-500" data-testid="chart-pertalife-ringkas">Dari <b>{tot}</b> pekerja aktif: Opsi 1 <b>{get("OPSI 1")}</b> · Opsi 2 <b>{get("OPSI 2")}</b> · terdaftar tanpa opsi <b>{get("TERDAFTAR (OPSI BELUM DIISI)")}</b> · <span className={tdk ? "text-rose-600 font-semibold" : ""}>tidak didaftarkan {tdk} ({tot ? Math.round(tdk / tot * 100) : 0}%)</span></p>); })()}
            </div>
          </div>
        </Card>
      </div>

  </>);
}
